// features/clients/clientsAPI.js

// Helper to get user from localStorage
const getUser = () => {
  try {
    const user = localStorage.getItem('admin_user');
    return user ? JSON.parse(user) : null;
  } catch (e) {
    return null;
  }
};

const assertSuccessfulEditResponse = (text, fallbackMessage) => {
  const responseText = String(text || '').trim();
  if (/fail|error|invalid|not\s*found|not\s*update|not\s*updated|unsuccess/i.test(responseText)) {
    throw new Error(responseText || fallbackMessage);
  }
};

const parseEditResponse = (text, fallbackMessage) => {
  assertSuccessfulEditResponse(text, fallbackMessage);

  try {
    const data = JSON.parse(text);
    if (data?.success === false || data?.status === false || data?.error) {
      throw new Error(data?.message || data?.error || fallbackMessage);
    }
    return data;
  } catch (error) {
    if (error instanceof SyntaxError) {
      return { message: text, success: true };
    }
    throw error;
  }
};

// ==================== CLIENT APIs ====================

// Fetch Full Clients (All)
export const fetchfullClientsAPI = async () => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/fullclients/getallfullrclients");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching full clients:", error);
    throw error;
  }
};

// Fetch Chunk Latest Clients (Streaming NDJSON)
export const fetchchunkLatestClientsAPI = async () => {
  try {
    const url = "https://alcobrakeadmin.com/v3/us/fullclients/getallfulllatestclients";
    const response = await fetch(url);

    if (!response.ok) throw new Error("Network response was not ok");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const clients = [];

    let done = false;
    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const client = JSON.parse(line);
          clients.push(client);
        } catch (err) {
          console.warn("Skipping invalid NDJSON line:", err);
        }
      }
    }

    if (buffer.trim()) {
      try {
        const client = JSON.parse(buffer);
        clients.push(client);
      } catch (err) {
        console.error("Invalid last NDJSON line:", err, buffer);
      }
    }

    return clients;
  } catch (error) {
    console.error("Error fetching chunk clients:", error);
    throw error;
  }
};

// Fetch Dealer Full Clients
export const fetchdealerfullClientsAPI = async (dealerId) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/fullclients/getdealerfullrclients?dealerName=${dealerId}`);
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching dealer clients:", error);
    throw error;
  }
};

// Fetch clients assigned to an office account.
export const fetchofficefullClientsAPI = async (officeId) => {
  const response = await fetch(`https://alcobrakeadmin.com/v3/us/fullclients/getofficefullclients?officeId=${encodeURIComponent(officeId)}`);
  if (!response.ok) throw new Error(`Failed to fetch office clients (${response.status})`);
  const data = await response.json();
  return data || [];
};

// Fetch Offices
export const fetchOfficesAPI = async () => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/caoffices/getalloffices");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching offices:", error);
    throw error;
  }
};

// Fetch Devices
export const fetchDevicesAPI = async () => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/cadevices/getalldevices");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching devices:", error);
    throw error;
  }
};

// Fetch Dealer Devices
export const fetchdealerDevicesAPI = async (dealerId) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/cadevices/getdealerdevices?dealerName=${dealerId}`);
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching dealer devices:", error);
    throw error;
  }
};

// Fetch Dealer Offices
export const fetchdealerOfficesAPI = async (dealerId) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/caoffices/getdealeroffices?dealerName=${dealerId}`);
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching dealer offices:", error);
    throw error;
  }
};

// Fetch Dealer Users
export const fetchdealerUsersAPI = async (dealerId) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/causers/getdealerusers?dealerName=${dealerId}`);
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching dealer users:", error);
    throw error;
  }
};

// Fetch Drivers
export const fetchDriversAPI = async () => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/caprohibs/getalldrivers");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching drivers:", error);
    throw error;
  }
};

// Fetch OLED Devices
export const fetchOLEDDevicesAPI = async () => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/app/v2data/getoleddevices");
    if (!response.ok) throw new Error(`Failed to fetch OLED devices (${response.status})`);
    const data = await response.json();
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    throw new Error('Invalid OLED device list response');
  } catch (error) {
    console.error("Error fetching OLED devices:", error);
    throw error;
  }
};

// Create OLED Device
export const createOLEDDeviceAPI = async (formData) => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/cadevices/adddevice", {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating OLED device:", error);
    throw error;
  }
};

// Edit OLED Device
export const editOLEDDeviceAPI = async (id, formData) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/cadevices/editdevice/${id}`, {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error editing OLED device:", error);
    throw error;
  }
};

// Delete OLED Device
export const deleteOLEDDeviceAPI = async (id) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/cadevices/deletedevice/${id}`, {
      method: 'GET'
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting OLED device:", error);
    throw error;
  }
};

// Get Serial Number
export const getOLEDSerialNumberAPI = async (state) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/cadevices/getserialnumber?state=${state}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting serial number:", error);
    throw error;
  }
};

// Create Full Client
export const createfullClientAPI = async (formData) => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/fullclients/addfullclient", {
      method: 'POST',
      body: formData
    });

    // Check if response is JSON or plain text
    const text = await response.text();

    if (!response.ok) {
      throw new Error(text || `Create client failed with status ${response.status}`);
    }
    return parseEditResponse(text, 'Create client failed');
  } catch (error) {
    console.error("Error creating client:", error);
    throw error;
  }
};

// Edit Full Client Prohibition
export const editfullProhibAPI = async (id, formData) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/fullclients/editfullprohib/${id}`, {
      method: 'POST',
      body: formData
    });

    // Check if response is JSON or plain text
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || `Edit prohibition failed with status ${response.status}`);
    }
    return parseEditResponse(text, 'Edit prohibition failed');
  } catch (error) {
    console.error("Error editing client prohibition:", error);
    throw error;
  }
};


// Edit Full Client
export const editfullClientAPI = async (id, formData) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/fullclients/editfullclient/${id}`, {
      method: 'POST',
      body: formData
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || `Edit client failed with status ${response.status}`);
    }
    return parseEditResponse(text, 'Edit client failed');
  } catch (error) {
    console.error("Error editing client:", error);
    throw error;
  }
};

// Delete Full Client (Add to Removals)
export const deleteFullclientAPI = async (clientData) => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/fullclients/deletefullclient", {
      method: 'POST',
      body: JSON.stringify(clientData),
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting client:", error);
    throw error;
  }
};

// Fetch Unique Full Client
export const fetchuniquefullClientAPI = async (clientId) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/fullclients/getuniquefullclient?clientId=${encodeURIComponent(clientId)}`);
    const data = await response.json();
    if (!response.ok || data?.status === false || data?.error) {
      throw new Error(data?.message || 'Unable to verify client details.');
    }
    // Return the data as is (array with one object)
    return data;
  } catch (error) {
    console.error("Error fetching unique client:", error);
    throw error;
  }
};


// Fetch Unique Invoice 2
export const fetchuniquerInvoice2API = async (regnum) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/cainvoices/getuniqueregInvoice2?regnum=${regnum}`);
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

// Create Invoice
export const createInvoiceAPI = async (invoiceData) => {
  try {
    const response = await fetch("https://alcobrakeadmin.com/v3/us/cainvoices/addinvoice", {
      method: 'POST',
      body: JSON.stringify(invoiceData),
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating invoice:", error);
    throw error;
  }
};

// ==================== LOGS APIs ====================

// Control ID Fetch Logs (Main log fetching function)
export const controlidfetchLogsAPI = async (cbid, installDate, fullclientid) => {
  try {
    const response = await fetch(`https://robopower.xyz/v2/eeprom/getalllogs6?fullclientid=${fullclientid}&installdate=${installDate}`);
    // const response = await fetch(
    //   `https://alcobrakeadmin.com/v3/v2/eeprom/getalllogs5?cbid=${cbid}&installdate=${installDate}`
    // );
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching logs:", error);
    throw error;
  }
};

// Get Device Timezone
export const getDeviceTimezoneAPI = async (sno) => {
  try {
    const response = await fetch(`https://alcobrakeadmin.com/v3/us/cadevices/getuniquedevice?sno=${sno}`);
    const data = await response.json();
    return data?.[0] || null;
  } catch (error) {
    console.error("Error fetching device timezone:", error);
    return null;
  }
};

// State Timezones Mapping
export const stateTimezones = {
  "AL": "America/Chicago", "AK": "America/Anchorage", "AZ": "America/Phoenix",
  "AR": "America/Chicago", "CA": "America/Los_Angeles", "CO": "America/Denver",
  "CT": "America/New_York", "DE": "America/New_York", "FL": "America/New_York",
  "GA": "America/New_York", "HI": "Pacific/Honolulu", "ID": "America/Boise",
  "IL": "America/Chicago", "IN": "America/Indiana/Indianapolis", "IA": "America/Chicago",
  "KS": "America/Chicago", "KY": "America/New_York", "LA": "America/Chicago",
  "ME": "America/New_York", "MD": "America/New_York", "MA": "America/New_York",
  "MI": "America/Detroit", "MN": "America/Chicago", "MS": "America/Chicago",
  "MO": "America/Chicago", "MT": "America/Denver", "NE": "America/Chicago",
  "NV": "America/Los_Angeles", "NH": "America/New_York", "NJ": "America/New_York",
  "NM": "America/Denver", "NY": "America/New_York", "NC": "America/New_York",
  "ND": "America/Chicago", "OH": "America/New_York", "OK": "America/Chicago",
  "OR": "America/Los_Angeles", "PA": "America/New_York", "RI": "America/New_York",
  "SC": "America/New_York", "SD": "America/Chicago", "TN": "America/Chicago",
  "TX": "America/Chicago", "UT": "America/Denver", "VT": "America/New_York",
  "VA": "America/New_York", "WA": "America/Los_Angeles", "WV": "America/New_York",
  "WI": "America/Chicago", "WY": "America/Denver", "INDIA": "Asia/Kolkata"
};
